import React, { useState } from 'react';
import { NavLink } from 'react-router-dom';
import PropTypes from 'prop-types';
import SaraLogo from '../svg/SaraLogo';
import SvgCompactDoubleArrowIcon from '../svg/SvgCompactDoubleArrowIcon';
import { FormattedMessage } from 'react-intl';
import { Button, buttonSizes, buttonTypes } from './Button';
import AccessControl from '../../AccessControl';
import permissions from '../../permissionsList';

/**
 * Nav bar component
 * This is duplicated in PHP so that the nav bar doesn't change from the PHP to React.
 * As top-level nav items are swapped into React, they can be swapped here from <a></a> tags to <NavLink></NavLink> Tags
 */

function Nav() {
  const [navMenuOpen, setNavMenuOpen] = useState(false);

  function menuToggle() {
    setNavMenuOpen(!navMenuOpen);
  }
  return (
    <React.Fragment>
      <div
        data-test="nav-wrapper"
        className={
          navMenuOpen
            ? 'nav__primary-nav-container nav__primary-nav-list--expanded'
            : 'nav__primary-nav-container'
        }>
        <div className="nav__primary-nav-logo-menu-container">
          <SaraLogo height="50px" />
          <FormattedMessage
            id="nav.menu"
            defaultMessage="Menu"
            description="Navigation button for the dropdown navigation menu">
            {(txt) => (
              <Button
                type="button"
                additionalStyles="nav__primary-nav-menu-button"
                onClick={menuToggle}
                rightIcon={<SvgCompactDoubleArrowIcon />}
                rightIconStyle="margin-left-5 text-medium"
                buttonType={buttonTypes.noButton}
                buttonSize={buttonSizes.extraSmall}
                text={txt}
                data-test="nav-menu-button"
              />
            )}
          </FormattedMessage>
        </div>
        <ul
          className={'noList nav__primary-nav-list'}
          role="navigation"
          aria-label="Main Navigation">
          <AccessControl
            data-test="access-control"
            feature={permissions.static.navBar.dashboard}>
            <li className="nav__primary-nav-list__item text-medium">
              <FormattedMessage
                id="nav.dashboard"
                defaultMessage="Dashboard"
                description="Primary navigation link to the main site dashboard"
                data-test="dashboard-link">
                {(txt) => (
                  <a id="nav-dashboard" href="/main/device_main.php">
                    {txt}
                  </a>
                )}
              </FormattedMessage>
            </li>
          </AccessControl>

          <AccessControl
            data-test="access-control"
            feature={permissions.static.navBar.administration}>
            <li className="nav__primary-nav-list__item text-medium">
              <FormattedMessage
                id="nav.administration"
                defaultMessage="Administration"
                description="Primary navigation link to the site administraion page"
                data-test="administration-link">
                {(txt) => (
                  <a id="nav-administration" href="/admin/adminscr.php">
                    {txt}
                  </a>
                )}
              </FormattedMessage>
            </li>
          </AccessControl>

          <AccessControl
            data-test="access-control"
            feature={permissions.static.navBar.installSetup}>
            <li className="nav__primary-nav-list__item text-medium">
              <FormattedMessage
                id="nav.installSetup"
                defaultMessage="Install Setup"
                description="Primary navigation link to the install setup page"
                data-test="install-setup-link">
                {(txt) => (
                  <a id="nav-install-setup" href="/admin/install_setup.php">
                    {txt}
                  </a>
                )}
              </FormattedMessage>
            </li>
          </AccessControl>

          <AccessControl
            data-test="access-control"
            feature={permissions.static.navBar.quickMessage}>
            <li className="nav__primary-nav-list__item text-medium">
              <FormattedMessage
                id="nav.quickMessage"
                defaultMessage="Quick Message"
                description="Primary navigation link to the quick message page"
                data-test="quick-message-link">
                {(txt) => (
                  <a id="nav-quick-message" href="/main/quick_msg.php">
                    {txt}
                  </a>
                )}
              </FormattedMessage>
            </li>
          </AccessControl>

          <AccessControl
            data-test="access-control"
            feature={permissions.static.navBar.reminders}>
            <li className="nav__primary-nav-list__item text-medium">
              <FormattedMessage
                id="nav.reminders"
                defaultMessage="Reminders"
                description="Primary navigation link to the reminders page"
                data-test="reminders-link">
                {(txt) => (
                  <a id="nav-reminders" href="/main/rem_list.php">
                    {txt}
                  </a>
                )}
              </FormattedMessage>
            </li>
          </AccessControl>

          <AccessControl
            data-test="messaging-access-control"
            feature={permissions.static.navBar.messaging}>
            <li>
              <FormattedMessage
                id="nav.messaging"
                defaultMessage="Messaging"
                description="Primary navigation link to the messaging page"
                data-test="messaging-link-text">
                {(txt) => (
                  <NavLink
                    id="nav-messaging"
                    to="/messaging/"
                    className={({ isActive }) =>
                      isActive
                        ? "nav__primary-nav-list__item text-medium nav__primary-nav-list__item--active"
                        : "nav__primary-nav-list__item text-medium"
                    }
                    data-test="messaging-link">
                    {txt}
                  </NavLink>
                )}
              </FormattedMessage>
            </li>
          </AccessControl>

          <AccessControl
            data-test="access-control"
            feature={permissions.static.navBar.reports}>
            <li className="nav__primary-nav-list__item text-medium">
              <FormattedMessage
                id="nav.reports"
                defaultMessage="Reports"
                description="Primary navigation link to the reports page"
                data-test="reports-link">
                {(txt) => (
                  <a id="nav-reports" href="/reports/reportscr.php">
                    {txt}
                  </a>
                )}
              </FormattedMessage>
            </li>
          </AccessControl>

          <AccessControl
            data-test="access-control"
            feature={permissions.static.navBar.users}>
            <li>
              <FormattedMessage
                id="nav.users"
                defaultMessage="Users"
                description="Primary navigation link to the users page"
                data-test="users-link-text">
                {(txt) => (
                  <NavLink
                    id="nav-users"
                    to="/users/"
                    className={({ isActive }) =>
                      isActive
                        ? "nav__primary-nav-list__item text-medium nav__primary-nav-list__item--active"
                        : "nav__primary-nav-list__item text-medium"
                    }
                    data-test="users-link">
                    {txt}
                  </NavLink>
                )}
              </FormattedMessage>
            </li>
          </AccessControl>

          <AccessControl
            data-test="access-control"
            feature={permissions.static.navBar.units}>
            <li>
              <FormattedMessage
                id="nav.units"
                defaultMessage="Units"
                description="Primary navigation link to the units (as in rooms) page"
                data-test="units-link-text">
                {(txt) => (
                  <NavLink
                    id="nav-units"
                    to="/units/"
                    className={({ isActive }) =>
                      isActive
                        ? "nav__primary-nav-list__item text-medium nav__primary-nav-list__item--active"
                        : "nav__primary-nav-list__item text-medium"
                    }
                    data-test="units-link">
                    {txt}
                  </NavLink>
                )}
              </FormattedMessage>
            </li>
          </AccessControl>
        </ul>
      </div>
    </React.Fragment>
  );
}

Nav.propTypes = {
  href: PropTypes.string,
};

export default Nav;
